import { useEffect, useState } from 'react';
import { Menu } from 'lucide-react';
import { formatLongDateEs, formatTimeEs, initials } from '@/shared/lib/format';
import { useAuthStore } from '@/shared/store/auth';

interface TopbarProps {
  title: string;
  subtitle?: string;
  /** Abre el drawer de navegación en móvil/tablet. */
  onMenuClick?: () => void;
}

export function Topbar({ title, subtitle, onMenuClick }: TopbarProps): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const userIni = user
    ? initials(user.nombre.split(' ')[0] ?? '', user.nombre.split(' ').slice(1).join(' ') || ' ')
    : 'EM';

  return (
    <header className="h-16 sm:h-[78px] bg-surface border-b border-border px-4 sm:px-6 lg:px-7 flex items-center gap-3 sm:gap-5 sticky top-0 z-40">
      {/* Hamburguesa — solo móvil/tablet */}
      <button
        type="button"
        onClick={onMenuClick}
        title="Abrir menú"
        aria-label="Abrir menú"
        className="lg:hidden -ml-1 p-2 rounded-md text-text-soft hover:bg-surface-alt hover:text-primary"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="min-w-0">
        <h1 className="m-0 text-lg sm:text-2xl font-extrabold tracking-tight text-text truncate">{title}</h1>
        {subtitle !== undefined && subtitle !== '' && (
          <p className="m-0 mt-0.5 text-[13px] text-text-muted truncate hidden sm:block">{subtitle}</p>
        )}
      </div>

      <div className="ml-auto text-right leading-tight hidden md:block">
        <div className="text-[12.5px] text-text-soft capitalize">{formatLongDateEs(now)}</div>
        <div className="text-base font-bold text-text mt-0.5">{formatTimeEs(now)}</div>
      </div>

      <div className="ml-auto md:ml-0 flex items-center gap-3 sm:pl-3 sm:border-l border-border h-11">
        <div className="text-right leading-tight hidden sm:block">
          <strong className="text-[13px] text-text font-bold block">{user?.nombre ?? 'Invitado'}</strong>
          <span className="text-[10.5px] text-text-muted uppercase tracking-wider block mt-0.5">
            {user?.rolLabel ?? '—'}
          </span>
        </div>
        <div className="w-9 h-9 sm:w-[42px] sm:h-[42px] rounded-full bg-gradient-to-br from-amber-200 to-amber-500 text-amber-900 grid place-items-center font-extrabold text-[13px] sm:text-[15px] border-2 border-white shadow-[0_2px_6px_rgba(245,158,11,0.25)]">
          {userIni}
        </div>
      </div>
    </header>
  );
}
